import { Inject, Injectable } from '@angular/core';
import { DwCustomContextDefinition, DwCustomContextBase, DwContextDefinition, DwQueryMdAttribute, DwAttributeType, DwControlType } from '@devwareapps/devware-cap';
import { BehaviorSubject } from 'rxjs';
import { of } from 'rxjs';
import { take } from 'rxjs/operators';
import { DwCacheService, DwStorageUtil, DwSecurityUserService, DwSecurityTopics, DwConfigService, DwConfigServiceToken, DwOrmDataService } from '@devwareapps/devware-cap';

export interface ChallengeContextData {
    ChallengeDefinitionId: number;
    ChallengeName: string;
}

@DwCustomContextDefinition({
    keyName: 'challenge-context',
    display: 'Selected Challenge',
    isGlobal: true,
})
@Injectable()
export class ChallengeContextService extends DwCustomContextBase {

    private _currentChallenge: ChallengeContextData = { ChallengeDefinitionId: 0, ChallengeName: '' };

    private challengeChanged$ = new BehaviorSubject<ChallengeContextData>(this._currentChallenge);
    challengeChange$ = this.challengeChanged$.asObservable();

    constructor(private cache: DwCacheService, private storage: DwStorageUtil,
        private security: DwSecurityUserService,
        @Inject(DwConfigServiceToken) private config: DwConfigService) {
        super();
        this.restoreSelection();
        this.security.securityContextChanged().subscribe(() => this.restoreSelection());
    }

    private selectionKey(): string {
        const c = this.security.securityContext;
        return 'Fitness.SelectedChallenge.' + JSON.stringify([this.config.coreConfig.apiRoot,
            c && c.ApiRoot, c && c.AuthenticatedUser && c.AuthenticatedUser.UserId,
            c && c.ApplicationUser && c.ApplicationUser.UserId]);
    }

    private get cacheOptions() {
        return { cacheInvalidationsTopics: [DwSecurityTopics.SECURITY_CHANGED,
            'Fitness.ReferenceData.Refresh', DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic('ChallengeDefinition')] };
    }

    private restoreSelection(): void {
        // CAP 4.8's cache storage reader omits its return. Use CAP storage for the session preference
        // and CAP cache for reuse. The selector validates the restored ID against current definitions.
        const saved = this.storage.session().getValue<ChallengeContextData>(this.selectionKey());
        const value = saved && Number.isInteger(saved.ChallengeDefinitionId) && saved.ChallengeDefinitionId > 0
            ? saved : { ChallengeDefinitionId: 0, ChallengeName: '' };
        // CAP can consume a synchronous fallback, invalidate it, and clear the in-flight
        // subject before returning. In that case it returns undefined rather than a stream.
        // Restore the validated session value so construction can still finish.
        const restored = this.cache.getWithOptions<ChallengeContextData>(this.selectionKey(), of(value), this.cacheOptions)
            || of(value);
        restored.pipe(take(1)).subscribe(c => {
            this._currentChallenge = c && Number.isInteger(c.ChallengeDefinitionId) && c.ChallengeDefinitionId > 0
                ? c : value;
            this.challengeChanged$.next(this._currentChallenge);
        });
    }

    get currentChallenge(): ChallengeContextData {
        return this._currentChallenge;
    }

    setChallenge(challenge: ChallengeContextData) {
        this.storage.session().setValue(this.selectionKey(), challenge);
        this.cache.setWithOptions(this.selectionKey(), challenge, this.cacheOptions);
        this._currentChallenge = challenge;
        this.challengeChanged$.next(challenge);
    }

    setupContextDefinition(contextDef: DwContextDefinition) {
        contextDef.noCache = true;
        contextDef.data = {};

        contextDef.getCustomContextFunction = (context: any, contextData: any) => {
            return {
                ChallengeDefinitionId: this._currentChallenge.ChallengeDefinitionId,
                ChallengeName: this._currentChallenge.ChallengeName,
            };
        };

        contextDef.attributes = this.getContextAttributes();
    }

    private getContextAttributes(): DwQueryMdAttribute[] {
        const attributes: DwQueryMdAttribute[] = [];
        this.addProperty(attributes, 'ChallengeDefinitionId', 'Challenge ID', DwAttributeType.Integer, DwControlType.NumericTextBox);
        this.addProperty(attributes, 'ChallengeName', 'Challenge Name', DwAttributeType.Text, DwControlType.Textbox);
        return attributes;
    }
}
